import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { AdfParser, AdfRepairStrategy, AdfSchemaValidator, AdfParseError } from '@atlassian/adf-parser';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type {
	StreamErrorCallback,
	StreamRepairCallback,
} from '../command-handlers/command-handler';
import type { ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';

import { getParserConfig } from './parser-config';
export class DocumentCreateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private contentLocation: ContentLocation;
	private actionId: string | undefined;
	private accumulated: string = '';
	private parser: AdfParser | undefined;
	private traceId: string | undefined;

	constructor(
		editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>,
		contentLocation: ContentLocation,
	) {
		this.editorApi = editorApi;
		this.contentLocation = contentLocation;
	}

	private init(traceId?: string) {
		this.editorApi.aiStreamingOrchestrator?.actions.startSession(
			fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
		);

		this.actionId = this.editorApi.aiStreamingOrchestrator?.actions.initAction(
			{
				type: 'appendNode',
				streamingType: 'create',
			},
			{
				/**
				 * Always hide the loading screen as staging area always shows Rovo conversation
				 * and will already have a loading indicator.
				 */
				hideLoadingScreen: this.contentLocation === 'staging-area',
			},
		);
		if (!this.actionId) {
			throw new Error('Failed to initialize streaming action for create');
		}

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
				config: getParserConfig(),
			});
		}

		log('Initialized editor streaming action', { streamType: 'create', actionId: this.actionId });
	}

	isInitialized(): boolean {
		return !!this.actionId;
	}

	stream(adf: string, isFinished: boolean = false, traceId: string | undefined): void {
		if (!this.actionId) {
			this.init(fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined);
		}

		if (!adf || !this.actionId) {
			return;
		}

		if (this.parser) {
			if (fg('platform_editor_ai_structured_parser_errors')) {
				try {
					this.parser.processChunk(adf, isFinished);
				} catch (error) {
					if (error instanceof AdfParseError) {
						// We want to ignore any parsing errors
					} else {
						// We still want to throw any other unexpected errors
						throw error;
					}
				}
			} else {
				this.parser.processChunk(adf, isFinished);
			}
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(
				// NOTE: This is a temporary solution before we move the streamer inside `/platform`
				this.actionId,
				JSON.stringify(this.parser.getPartialDocument()) ?? '',
			);
		} else {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		}
		if (traceId) {
			this.traceId = traceId;
		}

		this.accumulated += adf;
		log('Streaming content', { actionId: this.actionId, adf });
	}

	async finish({
		onError,
		onRepair,
	}: {
		onError?: StreamErrorCallback;
		onRepair?: StreamRepairCallback;
	}) {
		if (this.actionId) {
			this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
			await this.editorApi?.aiStreamingOrchestrator?.actions.endSession();
			log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });

			let finalDoc: DocNode | undefined;

			if (expValEquals('platform_editor_ai_create_use_new_parser', 'isEnabled', true)) {
				try {
					const schema = this.editorApi.core.sharedState.currentState()?.schema;
					if (!schema) {
						throw new Error('Schema is not available');
					}
					const parser = new AdfParser({
						schemaValidator: new AdfSchemaValidator(schema),
						repairStrategy: new AdfRepairStrategy(schema),
						config: getParserConfig(),
					});
					finalDoc = parser.parse(this.accumulated) as DocNode;
				} catch (error) {
					// If we fail here record it, and finalDoc is set to `undefined` -> this means that we'll retain
					// whatever we have streamed in, and we don't do any additional work as per the remaining logic
					log('New ADF Parser failed, falling back to ADF Streamer', { error });
					onError?.({ error, streamType: 'create', traceId: this.traceId });
				}
			} else {
				try {
					const schema = this.editorApi.core.sharedState.currentState()?.schema;
					if (!schema) {
						throw new Error('Schema is not available');
					}
					const parser = new AdfParser({
						schemaValidator: new AdfSchemaValidator(schema),
						repairStrategy: new AdfRepairStrategy(schema),
						config: getParserConfig(),
					});
					const doc = parser.parse(this.accumulated);

					const newStreamer = new ADFStreamer(schema);
					newStreamer.processChunk(JSON.stringify(doc || {}));
					finalDoc = newStreamer.getLastDoc();

					const repairs = parser.getRepairs();
					if (repairs.length > 0) {
						onRepair?.({ repairs, streamType: 'create' });
					}
				} catch (error) {
					log('New ADF Parser failed, falling back to ADF Streamer', { error });
					onError?.({ error, streamType: 'create', traceId: this.traceId });
					// Give ADF Streamer a chance to repair the ADF
					const schema = this.editorApi.core.sharedState.currentState()?.schema;
					if (schema) {
						const newStreamer = new ADFStreamer(schema);
						newStreamer.processChunk(this.accumulated);
						finalDoc = newStreamer.getLastDoc();
					}
				}
			}

			this.accumulated = '';
			this.actionId = undefined;
			this.traceId = undefined;

			return finalDoc;
		}
	}

	abort(): void {
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}
}
